import { useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import CallRoundedIcon from '@mui/icons-material/CallRounded';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import ExpandableImageComponent from '../Image/ExpandableImageComponent';

import { getBuildingResidentById } from '../Building/actionCreators';

import { avatarPlaceholderSRC } from '../../utils';
import { findPersonPhoneNumber, getFullName } from '../Person/utils';
import { getRoleNames } from './utils';

import { ActionButton } from '../Button';
import StyledLink from '../StyledLink';

const StyledPersonName = styled(({ profileUrl, ...props }) =>
  profileUrl ? (
    <StyledLink to={profileUrl} {...props} />
  ) : (
    <Typography {...props} />
  ),
)(({ theme }) => ({
  display: 'block',
  lineHeight: '1.6rem',
  color: theme.palette.primary.main,
}));

const StyledActionButton = styled(ActionButton)(() => ({
  display: 'flex',
  height: '3.5rem',
  borderRadius: '10rem',
}));

const FDPersonComponent = ({ resident, phone }) => {
  const { roles, profileUrl, buildingId } = resident;
  const {
    onCallButtonClick,
    hasActiveCall,
    showCallSettings,
    isCallAvailable,
  } = phone || {};

  const fullName = getFullName(resident);
  const roleNames = getRoleNames(roles);

  const dispatch = useDispatch();
  const getFullResidentNumber = async (residentId) => {
    const res = await getBuildingResidentById(dispatch, buildingId, residentId);
    const phoneNumber = findPersonPhoneNumber(res?.data);
    return phoneNumber;
  };

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Box sx={{ display: 'flex', margin: '1.5rem 0' }}>
        <Box mr="1rem">
          <ExpandableImageComponent
            src={avatarPlaceholderSRC}
            photo={resident?.photo}
            caption={fullName}
            height="3.2rem"
            isSquare
          />
        </Box>
        <Box>
          <StyledPersonName profileUrl={profileUrl}>
            {fullName}
          </StyledPersonName>
          <Box sx={{ fontSize: '1.4rem', lineHeight: '2rem', fontWeight: 600 }}>
            {roleNames}
          </Box>
        </Box>
      </Box>

      {Boolean(phone) && Boolean(resident.phoneNumber) && (
        <StyledActionButton
          onClick={async (e) => {
            if (!isCallAvailable) return showCallSettings();
            const phoneNumber = await getFullResidentNumber(resident.id);
            return onCallButtonClick(e, { ...resident, phoneNumber });
          }}
          disabled={hasActiveCall}
        >
          <CallRoundedIcon />
          Call
        </StyledActionButton>
      )}
      {Boolean(phone) && Boolean(!resident.phoneNumber) && (
        <Box
          sx={{
            fontSize: '1.4rem',
            lineHeight: '2rem',
            fontWeight: 600,
            textAlign: 'end',
          }}
        >
          No phone number
        </Box>
      )}
    </Box>
  );
};

FDPersonComponent.propTypes = {
  resident: PropTypes.instanceOf(Object).isRequired,
  phone: PropTypes.shape({
    onCallButtonClick: PropTypes.func,
    showCallSettings: PropTypes.func,
    hasActiveCall: PropTypes.bool,
    isCallAvailable: PropTypes.bool,
  }),
};

export default FDPersonComponent;
